import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import SingleBtn from 'components/common/elements/SingleBtn';
import ModalWrapper from 'components/common/overlays/ModalWrapper';
import FormDropdown from 'components/common/forms/FormDropdown';
import ChangePatient from './ChangePatient';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import 'swiper/css';
import 'swiper/css/pagination';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  RegisterEvaluationChartDefaultValues,
  TRegisterEvaluationChartDefaultValues,
} from 'constants/chart/common/chart';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { RegisterEvaluationChartFormField } from 'types/form/student';
import { TCheckSamePatientResponse } from 'types/apis/chart/studentEvaluationChart';
import { getChartRoundList, getCourseInfoList } from 'apis/common';
import { getAvaliableChartTypeList } from 'apis/student';
import useModal from 'hooks/useModal';
import useCheckChart, { TChangePatient } from 'hooks/chart/useCheckChart';
import useChartCreater from 'hooks/chart/useChartCreater';

type Tprops = {
  patientId: string;
};

const RegisterChartModal = ({ patientId }: Tprops) => {
  const navigate = useNavigate();

  const { openModal, closeModal } = useModal();

  const [deadline, setDeadline] = useState<string>('');
  const [registeredChartId, setRegisteredChartId] = useState<number>(0);
  const [submit, setSubmit] = useState<boolean>(false);
  const [checkData, setCheckData] = useState<TCheckSamePatientResponse>();

  const methods = useForm<TRegisterEvaluationChartDefaultValues>({
    defaultValues: RegisterEvaluationChartDefaultValues,
  });
  const { handleSubmit, control, setValue } = methods;

  const courseInfoId = useWatch({
    name: RegisterEvaluationChartFormField.CourseInfoId,
    control,
  });

  const chartRound = useWatch({
    name: RegisterEvaluationChartFormField.ChartRound,
    control,
  });

  const chartTypeNum = useWatch({
    name: RegisterEvaluationChartFormField.ChartTypeNum,
    control,
  });

  useEffect(() => {
    setValue(RegisterEvaluationChartFormField.ChartRound, undefined);
    setValue(RegisterEvaluationChartFormField.ChartTypeNum, undefined);
  }, [courseInfoId]);

  useEffect(() => {
    setDeadline(
      roundListWithDeadline.find((round) => round.chartRound === chartRound)
        ?.submitPeriod || ''
    );
    setValue(RegisterEvaluationChartFormField.ChartTypeNum, undefined);
  }, [chartRound]);

  useEffect(() => {
    setRegisteredChartId(
      chartListWithId.find((chart) => chart.chartTypeNum == chartTypeNum)
        ?.registeredChartId || 0
    );
  }, [chartTypeNum]);

  // 강의 목록
  const { data: getCourseInfoListData } = useQuery(
    'getCourseInfoList',
    getCourseInfoList
  );

  const courseList =
    getCourseInfoListData?.data?.map((course) => {
      return { label: course.courseName, value: course.courseInfoId };
    }) || [];

  // 회차 목록
  const { data: getChartRoundListData } = useQuery(
    ['getChartRoundList', courseInfoId],
    () => (courseInfoId ? getChartRoundList(courseInfoId.toString()) : null),
    {
      enabled: !!courseInfoId,
    }
  );

  const roundListWithDeadline = getChartRoundListData?.data || [];
  const roundList =
    roundListWithDeadline.map((round) => {
      return { label: round.registeredChartName, value: round.chartRound };
    }) || [];

  // 차트 종류 목록
  const { data: getAvaliableChartTypeListData } = useQuery(
    ['getAvaliableChartTypeList', courseInfoId, chartRound, deadline],
    () =>
      courseInfoId && chartRound
        ? getAvaliableChartTypeList(courseInfoId, {
            chartRound,
          })
        : null,
    {
      enabled: !!courseInfoId && !!chartRound && deadline.includes('작성 가능'),
    }
  );

  const chartListWithId = getAvaliableChartTypeListData?.data || [];
  const chartList =
    chartListWithId?.map((chart) => {
      return { label: chart.chartTypeName, value: chart.chartTypeNum };
    }) || [];

  // 차트 생성
  const goToChart = (evaluationChartId: number, evaluationRound: number) => {
    const chartRoute = ChartInfo.find((chart) => chart.type === chartTypeNum)
      ?.route;
    if (
      !chartRoute ||
      !evaluationChartId ||
      !patientId ||
      !courseInfoId ||
      !chartRound
    ) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    sessionStorage.setItem('route', 'patient');
    // 차트 작성 페이지로 이동
    navigate(
      `/evaluation${chartRoute}/${evaluationChartId}/register?patientId=${patientId}&courseInfoId=${courseInfoId}&chartRound=${chartRound}&tab=${
        evaluationRound === 2 ? 2 : 0
      }`
    );
  };

  const onChangePatient = ({
    patientId,
    courseInfoId,
    chartTypeNum,
    registeredChartId,
    isError,
    data,
  }: TChangePatient) => {
    setCheckData(data);
    openModal({
      element: (
        <ChangePatient
          isError={isError}
          chartRound={data.chartRound}
          patientName={data.patientName || ''}
          chartCount={data.evaluationChartCnt}
          onConfirm={(password) =>
            chartCreater({
              isSamePatient: data.isSamePatient,
              password: password,
              evaluationChartIdList: data.evaluationChartIdList,
              registerRequest: {
                patientId: Number(patientId),
                courseInfoId,
                registeredChartId,
                chartTypeNum,
              },
            })
          }
        />
      ),
    });
  };

  const { chartCreater } = useChartCreater({
    registeredChartId,
    courseInfoId,
    patientId,
    chartTypeNum,
    checkData,
    goToChart,
    onChangePatient,
  });

  useCheckChart({
    courseInfoId,
    chartRound,
    registeredChartId,
    chartTypeNum,
    patientId,
    submit,
    setSubmit,
    goToChart,
    createChart: (data) => chartCreater(data),
    onChangePatient,
  });

  return (
    <ModalWrapper>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(() => setSubmit(true))}>
          <div className="flex justify_between items_center">
            <h2 className="main_title">실습용 차트 선택</h2>
            <button type="button" onClick={closeModal}>
              <CloseIcon fill={STYLES_VARIABLES.GRAY_08} />
            </button>
          </div>

          <div className="flex flex_column mt32">
            <div className="flex flex_column gap16">
              <FormDropdown
                name={RegisterEvaluationChartFormField.CourseInfoId}
                placeholder="차트를 제출할 강의를 선택해 주세요."
                selectList={courseList}
                btnStyle={{ width: '432px', height: '48px' }}
                dropdownStyle={{ width: '432px' }}
              />
              <div>
                <FormDropdown
                  name={RegisterEvaluationChartFormField.ChartRound}
                  placeholder="등록할 회차(회차명)를 선택해 주세요."
                  selectList={roundList}
                  btnStyle={{ width: '432px', height: '48px' }}
                  dropdownStyle={{ width: '432px' }}
                />
                {chartRound && (
                  <p className="mt8 fs12 fw500 font_red">{deadline}</p>
                )}
              </div>
            </div>

            <p className="mt24 lh22 fs14 fw700">
              등록할 차트 종류를 선택해 주세요.
            </p>
            <FormDropdown
              name={RegisterEvaluationChartFormField.ChartTypeNum}
              placeholder="차트 종류를 선택해 주세요."
              selectList={chartList}
              btnStyle={{ width: '432px', height: '48px', marginTop: '8px' }}
              dropdownStyle={{ width: '432px' }}
            />
          </div>

          <div className="modal_btn_wrapper mt32">
            <SingleBtn
              btnText="취소"
              hasOutline={true}
              btnStyle={{
                width: '100%',
              }}
              onClick={closeModal}
            />
            <SingleBtn
              type="submit"
              btnText="확인"
              isBtnActive={true}
              btnStyle={{
                width: '100%',
              }}
              disabled={
                !courseInfoId ||
                !chartRound ||
                !chartTypeNum ||
                !registeredChartId ||
                !deadline.includes('작성 가능')
              }
            />
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
};

export default RegisterChartModal;
